import { Ionicons } from "@expo/vector-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Print from "expo-print";
import { Link } from "expo-router";
import * as Sharing from "expo-sharing";
import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  useColorScheme,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { despesasRepository } from "../repositories/despesasRepository";
import { vendasRepository } from "../repositories/vendasRepository";
import { StatusVenda, VendaComItens } from "../types/database";
import { confirmarAcaoWebOuNativo } from "../utils/confirmarAcaoWebOuNativo";
import { formatarDataBr } from "../utils/dataVencimento";
import { obterPeriodoMes } from "../utils/periodoMes";
import { abrirWhatsAppCobranca } from "../utils/whatsapp";

type FiltroComandas = "todas" | "pendentes" | "pagas";

const FILTROS: { id: FiltroComandas; label: string }[] = [
  { id: "todas", label: "Todas" },
  { id: "pendentes", label: "Pendentes" },
  { id: "pagas", label: "Pagas" },
];

function filtrarComandas(
  vendas: VendaComItens[],
  filtro: FiltroComandas,
): VendaComItens[] {
  const vendasVisiveis = vendas.filter((v) => v.arquivado !== true);

  function getTimestampVencimento(vencimento: string | Date): number {
    if (vencimento instanceof Date) {
      return Number.isNaN(vencimento.getTime())
        ? Number.POSITIVE_INFINITY
        : vencimento.getTime();
    }

    const valor = String(vencimento).trim();
    const matchData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);

    if (matchData) {
      const ano = Number(matchData[1]);
      const mes = Number(matchData[2]);
      const dia = Number(matchData[3]);
      return Date.UTC(ano, mes - 1, dia);
    }

    const timestamp = new Date(valor).getTime();
    return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
  }

  function ordenarPendentesPorVencimentoAsc(
    lista: VendaComItens[],
  ): VendaComItens[] {
    return [...lista].sort((a, b) => {
      const diferencaVencimento =
        getTimestampVencimento(a.vencimento) -
        getTimestampVencimento(b.vencimento);

      if (diferencaVencimento !== 0) return diferencaVencimento;

      const createdA = new Date(a.created_at).getTime();
      const createdB = new Date(b.created_at).getTime();
      return createdB - createdA;
    });
  }

  switch (filtro) {
    case "pendentes":
      return ordenarPendentesPorVencimentoAsc(
        vendasVisiveis.filter(
          (v) => v.status === "Pendente" || v.status === "Enviado",
        ),
      );
    case "pagas":
      return vendasVisiveis.filter((v) => v.status === "Pago");
    default:
      return vendasVisiveis;
  }
}

function getStatusEstilo(status: StatusVenda) {
  switch (status) {
    case "Pendente":
      return { bg: "#fef3c7", text: "#92400e", label: "Pendente" };
    case "Enviado":
      return { bg: "#dbeafe", text: "#1e40af", label: "Enviado" };
    case "Pago":
      return { bg: "#dcfce7", text: "#166534", label: "Pago" };
  }
}

function escaparHtml(valor: string) {
  return valor
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default function TelaComandas() {
  const queryClient = useQueryClient();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const styles = createStyles(isDark);
  const [filtro, setFiltro] = useState<FiltroComandas>("todas");
  const [vendaEmAcao, setVendaEmAcao] = useState<string | null>(null);
  const [isGerandoRelatorio, setIsGerandoRelatorio] = useState(false);
  const [dataReferencia, setDataReferencia] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const periodoMes = obterPeriodoMes(dataReferencia);
  const mesAnoSelecionado = dataReferencia.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
  const mesAnoAtual = new Date().toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
  const estaNoMesAtual = mesAnoSelecionado === mesAnoAtual;

  const {
    data: vendas,
    isLoading,
    error,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ["vendas"],
    queryFn: vendasRepository.listarTodas,
    refetchInterval: 15_000,
  });

  const {
    data: vendasResumoMes,
    isLoading: isLoadingResumo,
    isFetching: isFetchingResumo,
    error: errorResumo,
  } = useQuery({
    queryKey: [
      "vendas-resumo-mes",
      dataReferencia.getFullYear(),
      dataReferencia.getMonth(),
    ],
    queryFn: () =>
      vendasRepository.listarDoMesParaResumo(
        periodoMes.inicioIso,
        periodoMes.fimIso,
      ),
    refetchInterval: 15_000,
  });

  const {
    data: despesas,
    isLoading: isLoadingDespesas,
    isFetching: isFetchingDespesas,
    error: errorDespesas,
  } = useQuery({
    queryKey: [
      "despesas-mes",
      dataReferencia.getFullYear(),
      dataReferencia.getMonth(),
    ],
    queryFn: () =>
      despesasRepository.listarDoMes(
        periodoMes.inicioBanco,
        periodoMes.fimBanco,
      ),
    refetchInterval: 15_000,
  });

  const vendasFiltradas = useMemo(
    () => filtrarComandas(vendas ?? [], filtro),
    [vendas, filtro],
  );

  //console.log("Dados para o resumo:", vendas);

  const totalReceber =
    vendasResumoMes
      ?.filter(
        (v) =>
          v.status?.toLowerCase() === "pendente" ||
          v.status?.toLowerCase() === "enviado",
      )
      ?.reduce((acc, v) => acc + Number(v.valor_total || 0), 0) || 0;

  const totalRecebido =
    vendasResumoMes
      ?.filter((v) => v.status?.toLowerCase() === "pago")
      ?.reduce((acc, v) => acc + Number(v.valor_total || 0), 0) || 0;

  const totalDespesas =
    despesas?.reduce((acc, despesa) => acc + Number(despesa.valor || 0), 0) ||
    0;

  const lucroLivre = totalRecebido - totalDespesas;

  async function gerarRelatorioPDF() {
    setIsGerandoRelatorio(true);

    try {
      const agora = new Date();
      const mesAno = mesAnoSelecionado;
      const periodo = mesAno.charAt(0).toUpperCase() + mesAno.slice(1);
      const titulo = `Relatório de Fechamento - ${periodo}`;
      const linhasDespesas = (despesas ?? []).length
        ? (despesas ?? [])
            .map(
              (despesa) => `
                <tr>
                  <td>${escaparHtml(formatarDataBr(despesa.data_compra))}</td>
                  <td>${escaparHtml(despesa.descricao)}</td>
                  <td class="valor">R$ ${Number(despesa.valor).toFixed(2)}</td>
                </tr>`,
            )
            .join("")
        : `<tr><td class="vazio" colspan="3">Nenhuma despesa registrada no período.</td></tr>`;

      const htmlString = `
        <!DOCTYPE html>
        <html lang="pt-BR">
          <head>
            <meta charset="UTF-8" />
            <style>
              * { box-sizing: border-box; }
              body { font-family: Arial, sans-serif; color: #1f2937; margin: 0; padding: 32px; }
              header { border-bottom: 3px solid #2563eb; padding-bottom: 18px; margin-bottom: 24px; }
              h1 { color: #1d4ed8; font-size: 25px; margin: 0 0 6px; }
              .periodo { color: #6b7280; font-size: 14px; margin: 0; }
              h2 { color: #1f2937; font-size: 17px; margin: 24px 0 12px; }
              .balanco { border: 1px solid #dbeafe; border-radius: 8px; padding: 16px; }
              .linha { display: flex; justify-content: space-between; padding: 9px 0; border-bottom: 1px solid #e5e7eb; }
              .linha:last-child { border-bottom: 0; }
              .grupo { color: #6b7280; font-size: 12px; text-transform: uppercase; }
              .valor { font-weight: bold; text-align: right; }
              .resultado { color: #1d4ed8; font-size: 16px; }
              table { border-collapse: collapse; width: 100%; font-size: 13px; }
              th { background: #eff6ff; color: #1e40af; font-weight: bold; text-align: left; }
              th, td { border-bottom: 1px solid #e5e7eb; padding: 10px 8px; }
              .vazio { color: #6b7280; text-align: center; padding: 18px; }
              footer { color: #9ca3af; font-size: 11px; margin-top: 28px; text-align: center; }
            </style>
          </head>
          <body>
            <header>
              <h1>Açai do Casal20</h1>
              <p class="periodo">${escaparHtml(titulo)}</p>
            </header>
            <h2>Balanço Financeiro</h2>
            <section class="balanco">
              <div class="linha"><span><span class="grupo">Entradas</span><br />Total Recebido</span><span class="valor">R$ ${totalRecebido.toFixed(2)}</span></div>
              <div class="linha"><span><span class="grupo">Entradas</span><br />Total a Receber</span><span class="valor">R$ ${totalReceber.toFixed(2)}</span></div>
              <div class="linha"><span><span class="grupo">Saídas</span><br />Total Despesas</span><span class="valor">R$ ${totalDespesas.toFixed(2)}</span></div>
              <div class="linha resultado"><span>Resultado<br /><strong>Lucro Livre</strong></span><span class="valor">R$ ${lucroLivre.toFixed(2)}</span></div>
            </section>
            <h2>Extrato de Despesas</h2>
            <table>
              <thead><tr><th>Data</th><th>Descrição</th><th class="valor">Valor</th></tr></thead>
              <tbody>${linhasDespesas}</tbody>
            </table>
            <footer>Relatório gerado em ${agora.toLocaleDateString("pt-BR")}</footer>
          </body>
        </html>`;

      const { uri } = await Print.printToFileAsync({ html: htmlString });
      await Sharing.shareAsync(uri);
    } catch (err) {
      Alert.alert(
        "Erro",
        err instanceof Error
          ? err.message
          : "Não foi possível gerar o relatório PDF.",
      );
    } finally {
      setIsGerandoRelatorio(false);
    }

  }

  function navegarMes(direcao: number) {
    setDataReferencia(
      (mesAtual) =>
        new Date(mesAtual.getFullYear(), mesAtual.getMonth() + direcao, 1),
    );
  }

  function voltarAoMesAtual() {
    const hoje = new Date();
    setDataReferencia(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  }

  const atualizarStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusVenda }) =>
      vendasRepository.atualizarStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendas"] });
      queryClient.invalidateQueries({ queryKey: ["vendas-resumo-mes"] });
    },
    onError: (err: Error) => {
      Alert.alert(
        "Erro",
        err.message || "Não foi possível atualizar o status.",
      );
    },
    onSettled: () => {
      setVendaEmAcao(null);
    },
  });

  const deletarVendaMutation = useMutation({
    mutationFn: (id: string) => vendasRepository.deletarVenda(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendas"] });
      queryClient.invalidateQueries({ queryKey: ["vendas-resumo-mes"] });
    },
    onError: (err: Error) => {
      Alert.alert("Erro", err.message || "Não foi possível excluir a venda.");
    },
    onSettled: () => {
      setVendaEmAcao(null);
    },
  });

  const arquivarTodosPagosMutation = useMutation({
    mutationFn: () => vendasRepository.arquivarTodosPagos(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendas"] });
      queryClient.invalidateQueries({ queryKey: ["vendas-resumo-mes"] });
    },
    onError: (err: Error) => {
      Alert.alert(
        "Erro",
        err.message || "Não foi possível arquivar os pedidos pagos.",
      );
    },
  });

  async function handleCobrarWhatsApp(venda: VendaComItens) {
    setVendaEmAcao(venda.id);
    try {
      await abrirWhatsAppCobranca(venda);
      if (venda.status !== "Enviado" && venda.status !== "Pago") {
        await atualizarStatusMutation.mutateAsync({
          id: venda.id,
          status: "Enviado",
        });
      }
    } catch (err) {
      setVendaEmAcao(null);
      Alert.alert(
        "Erro",
        err instanceof Error ? err.message : "Erro ao abrir WhatsApp.",
      );
    }
  }

  function handleMarcarComoPago(venda: VendaComItens) {
    if (venda.status === "Pago") return;

    confirmarAcaoWebOuNativo({
      titulo: "Confirmar pagamento",
      mensagem: `Marcar comanda de ${venda.cliente_nome} como paga?`,
      onConfirmar: () => {
        setVendaEmAcao(venda.id);
        atualizarStatusMutation.mutate({ id: venda.id, status: "Pago" });
      },
    });
  }

  function handleExcluirVenda(venda: VendaComItens) {
    confirmarAcaoWebOuNativo({
      titulo: "Confirmar arquivamento",
      mensagem: `Arquivar a comanda de ${venda.cliente_nome}?`,
      onConfirmar: () => {
        setVendaEmAcao(venda.id);
        deletarVendaMutation.mutate(venda.id);
      },
      destructive: true,
      textoConfirmar: "Arquivar",
    });
  }

  function handleArquivarTodosPagos() {
    confirmarAcaoWebOuNativo({
      titulo: "Confirmar arquivamento em lote",
      mensagem: "Tem certeza que deseja arquivar todos os pedidos pagos?",
      onConfirmar: () => arquivarTodosPagosMutation.mutate(),
      destructive: true,
      textoConfirmar: "Arquivar todos",
    });
  }

  function renderComanda({ item }: { item: VendaComItens }) {
    const statusEstilo = getStatusEstilo(item.status);
    const emAcao =
      vendaEmAcao === item.id &&
      (atualizarStatusMutation.isPending || deletarVendaMutation.isPending);

    return (
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.cardTitulo}>
            <Text style={styles.clienteNome}>
              {item.graduacao === "Civil" || !item.graduacao
                ? item.cliente_nome
                : `${item.graduacao} ${item.cliente_nome}`}
            </Text>
            <Text style={styles.clienteTelefone}>{item.telefone}</Text>
          </View>
          <View style={[styles.badge, { backgroundColor: statusEstilo.bg }]}>
            <Text style={[styles.badgeTexto, { color: statusEstilo.text }]}>
              {statusEstilo.label}
            </Text>
          </View>
        </View>

        <View style={styles.cardInfo}>
          {item.modalidade === "a prazo" ? (
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Vencimento</Text>
              <Text style={styles.infoValor}>
                {formatarDataBr(item.vencimento)}
              </Text>
            </View>
          ) : (
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Pagamento</Text>
              <Text style={styles.infoValor}>Na hora</Text>
            </View>
          )}
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Total</Text>
            <Text style={styles.infoValorDestaque}>
              R$ {item.valor_total.toFixed(2)}
            </Text>
          </View>
        </View>

        {item.itens_venda.length > 0 && (
          <View style={styles.itensResumo}>
            {item.itens_venda.map((vendaItem) => (
              <Text key={vendaItem.id} style={styles.itemLinha}>
                {vendaItem.quantidade}x{" "}
                {vendaItem.nome_produto ??
                  vendaItem.produtos?.nome ??
                  "Produto"}{" "}
                — R$ {vendaItem.subtotal.toFixed(2)}
              </Text>
            ))}
          </View>
        )}

        <View style={styles.acoes}>
          <Pressable
            style={[styles.botaoWhatsApp, emAcao && styles.botaoDesabilitado]}
            onPress={() => handleCobrarWhatsApp(item)}
            disabled={emAcao}
          >
            {emAcao ? (
              <ActivityIndicator color="#fff" size="small" />
            ) : (
              <>
                <Ionicons name="logo-whatsapp" size={20} color="#fff" />
                <Text style={styles.botaoWhatsAppTexto}>
                  Cobrar no WhatsApp
                </Text>
              </>
            )}
          </Pressable>

          {item.status !== "Pago" && (
            <Pressable
              style={[styles.botaoPago, emAcao && styles.botaoDesabilitado]}
              onPress={() => handleMarcarComoPago(item)}
              disabled={emAcao}
            >
              <Ionicons
                name="checkmark-circle-outline"
                size={20}
                color="#166534"
              />
              <Text style={styles.botaoPagoTexto}>Marcar como Pago</Text>
            </Pressable>
          )}

          {item.status === "Pago" && (
            <Pressable
              style={[styles.botaoExcluir, emAcao && styles.botaoDesabilitado]}
              onPress={() => handleExcluirVenda(item)}
              disabled={emAcao}
            >
              <Ionicons name="trash-outline" size={18} color="#fff" />
            </Pressable>
          )}
        </View>
      </View>
    );
  }

  if (isLoading || isLoadingResumo || isLoadingDespesas) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563eb" />
        <Text style={styles.textoCarregando}>Carregando dados...</Text>
      </View>
    );
  }

  if (error || errorResumo || errorDespesas) {
    return (
      <View style={styles.center}>
        <Text style={styles.textoErro}>Erro ao carregar dados do app.</Text>
        <Pressable style={styles.botaoRetry} onPress={() => refetch()}>
          <Text style={styles.botaoRetryTexto}>Tentar novamente</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <View style={styles.header}>
        <Text style={styles.titulo}>Comandas</Text>
        <View style={styles.headerAcoes}>
          {isRefetching && <ActivityIndicator size="small" color="#2563eb" />}
          <Link href="/configuracoes" asChild>
            <Pressable style={styles.botaoHeader} hitSlop={8}>
              <Ionicons name="settings-outline" size={22} color="#2563eb" />
            </Pressable>
          </Link>
          <Link href="/produtos" asChild>
            <Pressable style={styles.botaoHeader} hitSlop={8}>
              <Ionicons name="cube-outline" size={24} color="#2563eb" />
            </Pressable>
          </Link>
          <Link href="/despesas" asChild>
            <Pressable style={styles.botaoHeader} hitSlop={8}>
              <Ionicons
                name="trending-down-outline"
                size={22}
                color="#2563eb"
              />
            </Pressable>
          </Link>
        </View>
      </View>

      <View style={styles.resumoContainer}>
        <Text style={styles.resumoTitulo}>Resumo financeiro</Text>
        <View style={styles.seletorMes}>
          <Pressable
            style={styles.botaoMes}
            onPress={() => navegarMes(-1)}
            disabled={isFetchingResumo || isFetchingDespesas}
            hitSlop={8}
          >
            <Ionicons name="chevron-back" size={22} color="#2563eb" />
          </Pressable>
          <Pressable
            style={styles.mesSelecionado}
            onPress={voltarAoMesAtual}
            disabled={estaNoMesAtual || isFetchingResumo || isFetchingDespesas}
          >
            <Text style={styles.mesSelecionadoTexto}>
              {mesAnoSelecionado.charAt(0).toUpperCase() +
                mesAnoSelecionado.slice(1)}
            </Text>
            {!estaNoMesAtual && (
              <Text style={styles.voltarMesTexto}>Voltar ao mês atual</Text>
            )}
          </Pressable>
          <Pressable
            style={styles.botaoMes}
            onPress={() => navegarMes(1)}
            disabled={isFetchingResumo || isFetchingDespesas}
            hitSlop={8}
          >
            <Ionicons name="chevron-forward" size={22} color="#2563eb" />
          </Pressable>
        </View>
        {(isFetchingResumo || isFetchingDespesas) && (
          <ActivityIndicator
            style={styles.carregandoMes}
            size="small"
            color="#2563eb"
          />
        )}
        <View style={styles.resumoGrid}>
          <View style={[styles.resumoCard, styles.resumoCardPendente]}>
            <Text style={styles.resumoLabel}>Total a receber</Text>
            <Text style={styles.resumoValorPendente}>
              R$ {totalReceber.toFixed(2)}
            </Text>
          </View>

          <View style={[styles.resumoCard, styles.resumoCardRecebido]}>
            <Text style={styles.resumoLabel}>Total recebido</Text>
            <Text style={styles.resumoValorRecebido}>
              R$ {totalRecebido.toFixed(2)}
            </Text>
          </View>
        </View>

        <View style={[styles.resumoGrid, styles.resumoGridInferior]}>
          <View style={[styles.resumoCard, styles.resumoCardDespesa]}>
            <Text style={styles.resumoLabel}>Gastos</Text>
            <Text style={styles.resumoValorDespesa}>
              -R$ {totalDespesas.toFixed(2)}
            </Text>
          </View>

          <View style={[styles.resumoCard, styles.resumoCardLucro]}>
            <Text style={styles.resumoLabel}>Lucro livre</Text>
            <Text
              style={
                lucroLivre >= 0
                  ? styles.resumoValorLucroPositivo
                  : styles.resumoValorLucroNegativo
              }
            >
              R$ {lucroLivre.toFixed(2)}
            </Text>
          </View>
        </View>
      </View>

      <Pressable
        style={[
          styles.botaoRelatorio,
          isGerandoRelatorio && styles.botaoDesabilitado,
        ]}
        onPress={gerarRelatorioPDF}
        disabled={isGerandoRelatorio}
      >
        {isGerandoRelatorio ? (
          <ActivityIndicator color="#fff" size="small" />
        ) : (
          <Ionicons name="document-text-outline" size={20} color="#fff" />
        )}
        <Text style={styles.botaoRelatorioTexto}>
          {isGerandoRelatorio ? "Gerando relatório..." : "Baixar Relatório PDF"}
        </Text>
      </Pressable>

      <View style={styles.filtros}>
        {FILTROS.map((opcao) => (
          <Pressable
            key={opcao.id}
            style={[
              styles.filtroBotao,
              filtro === opcao.id && styles.filtroBotaoAtivo,
            ]}
            onPress={() => setFiltro(opcao.id)}
          >
            <Text
              style={[
                styles.filtroTexto,
                filtro === opcao.id && styles.filtroTextoAtivo,
              ]}
            >
              {opcao.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {filtro === "pagas" && (
        <Pressable
          style={[
            styles.botaoArquivarTodos,
            arquivarTodosPagosMutation.isPending && styles.botaoDesabilitado,
          ]}
          onPress={handleArquivarTodosPagos}
          disabled={arquivarTodosPagosMutation.isPending}
        >
          {arquivarTodosPagosMutation.isPending ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <>
              <Ionicons name="archive-outline" size={18} color="#fff" />
              <Text style={styles.botaoArquivarTodosTexto}>
                Arquivar todos os pagos
              </Text>
            </>
          )}
        </Pressable>
      )}

      <FlatList
        data={vendasFiltradas}
        keyExtractor={(item) => item.id}
        renderItem={renderComanda}
        contentContainerStyle={styles.lista}
        refreshing={isRefetching}
        onRefresh={refetch}
        ListEmptyComponent={
          <View style={styles.listaVazia}>
            <Text style={styles.listaVaziaTexto}>
              Nenhuma comanda encontrada.
            </Text>
          </View>
        }
      />

      <Link href="/nova-venda" asChild>
        <Pressable style={styles.fab}>
          <Ionicons name="add" size={28} color="#fff" />
        </Pressable>
      </Link>
    </SafeAreaView>
  );
}

function createStyles(isDark: boolean) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: isDark ? "#121212" : "#F5F5F5" },
    center: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
      backgroundColor: isDark ? "#121212" : "#F5F5F5",
    },
    textoCarregando: { marginTop: 12, color: isDark ? "#AAAAAA" : "#666" },
    textoErro: { color: "#dc2626", marginBottom: 16 },
    botaoRetry: {
      backgroundColor: "#2563eb",
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 8,
    },
    botaoRetryTexto: { color: "#fff", fontWeight: "600" },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 20,
      paddingTop: 8,
      paddingBottom: 12,
      backgroundColor: isDark ? "#121212" : "#F5F5F5",
    },
    headerAcoes: { flexDirection: "row", alignItems: "center", gap: 12 },
    botaoHeader: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: isDark ? "#1E1E1E" : "#eff6ff",
      justifyContent: "center",
      alignItems: "center",
    },
    titulo: {
      fontSize: 28,
      fontWeight: "bold",
      color: isDark ? "#FFFFFF" : "#111",
    },
    resumoContainer: {
      marginHorizontal: 20,
      marginBottom: 12,
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 16,
      padding: 16,
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#e5e7eb",
    },
    resumoTitulo: {
      fontSize: 16,
      fontWeight: "700",
      color: isDark ? "#FFFFFF" : "#111827",
      marginBottom: 12,
    },
    seletorMes: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 44,
      marginBottom: 12,
      borderRadius: 10,
      backgroundColor: isDark ? "#252525" : "#f8fafc",
      borderWidth: 1,
      borderColor: isDark ? "#3A3A3A" : "#e2e8f0",
    },
    botaoMes: {
      width: 44,
      height: 44,
      alignItems: "center",
      justifyContent: "center",
    },
    mesSelecionado: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      minHeight: 44,
    },
    mesSelecionadoTexto: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? "#F9FAFB" : "#1f2937",
    },
    voltarMesTexto: {
      marginTop: 2,
      fontSize: 11,
      color: "#2563eb",
    },
    carregandoMes: {
      marginBottom: 8,
    },
    resumoGrid: {
      flexDirection: "row",
      gap: 12,
    },
    resumoGridInferior: {
      marginTop: 12,
    },
    resumoCard: {
      flex: 1,
      borderRadius: 12,
      padding: 12,
    },
    resumoCardPendente: {
      backgroundColor: isDark ? "#3B2F1D" : "#fff7ed",
      borderWidth: 1,
      borderColor: isDark ? "#8A633A" : "#fed7aa",
    },
    resumoCardRecebido: {
      backgroundColor: isDark ? "#1A362C" : "#ecfdf5",
      borderWidth: 1,
      borderColor: isDark ? "#2B7A5E" : "#a7f3d0",
    },
    resumoCardDespesa: {
      backgroundColor: isDark ? "#3B1E1E" : "#fef2f2",
      borderWidth: 1,
      borderColor: isDark ? "#7F3D3D" : "#fecaca",
    },
    resumoCardLucro: {
      backgroundColor: isDark ? "#1F2C3A" : "#edf6ff",
      borderWidth: 1,
      borderColor: isDark ? "#406A8D" : "#bfdbfe",
    },
    resumoLabel: {
      fontSize: 12,
      fontWeight: "600",
      color: isDark ? "#D1D5DB" : "#4b5563",
      marginBottom: 6,
    },
    resumoValorPendente: {
      fontSize: 20,
      fontWeight: "800",
      color: isDark ? "#FBBF24" : "#b45309",
    },
    resumoValorRecebido: {
      fontSize: 20,
      fontWeight: "800",
      color: isDark ? "#4ADE80" : "#047857",
    },
    resumoValorDespesa: {
      fontSize: 20,
      fontWeight: "800",
      color: isDark ? "#FCA5A5" : "#b91c1c",
    },
    resumoValorLucroPositivo: {
      fontSize: 20,
      fontWeight: "800",
      color: isDark ? "#93C5FD" : "#1d4ed8",
    },
    resumoValorLucroNegativo: {
      fontSize: 20,
      fontWeight: "800",
      color: isDark ? "#FCA5A5" : "#b91c1c",
    },
    botaoRelatorio: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      marginHorizontal: 20,
      marginBottom: 12,
      borderRadius: 8,
      paddingVertical: 12,
      backgroundColor: "#2563eb",
    },
    botaoRelatorioTexto: { color: "#fff", fontWeight: "700", fontSize: 15 },
    botaoArquivarTodos: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      marginHorizontal: 20,
      marginBottom: 12,
      borderRadius: 8,
      paddingVertical: 12,
      backgroundColor: "#b91c1c",
    },
    botaoArquivarTodosTexto: {
      color: "#fff",
      fontWeight: "700",
      fontSize: 14,
    },
    filtros: {
      flexDirection: "row",
      paddingHorizontal: 20,
      gap: 8,
      marginBottom: 12,
    },
    filtroBotao: {
      flex: 1,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: isDark ? "#2A2A2A" : "#e5e7eb",
      alignItems: "center",
    },
    filtroBotaoAtivo: { backgroundColor: "#2563eb" },
    filtroTexto: {
      fontSize: 14,
      fontWeight: "600",
      color: isDark ? "#D1D5DB" : "#4b5563",
    },
    filtroTextoAtivo: { color: "#fff" },
    lista: { paddingHorizontal: 20, paddingBottom: 100 },
    listaVazia: { paddingVertical: 40, alignItems: "center" },
    listaVaziaTexto: { color: isDark ? "#AAAAAA" : "#6b7280", fontSize: 16 },
    card: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#e5e7eb",
    },
    cardHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 12,
    },
    cardTitulo: { flex: 1, marginRight: 12 },
    clienteNome: {
      fontSize: 18,
      fontWeight: "bold",
      color: isDark ? "#FFFFFF" : "#111",
    },
    clienteTelefone: {
      fontSize: 14,
      color: isDark ? "#AAAAAA" : "#6b7280",
      marginTop: 2,
    },
    badge: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 12,
    },
    badgeTexto: { fontSize: 12, fontWeight: "700" },
    cardInfo: {
      flexDirection: "row",
      gap: 24,
      marginBottom: 12,
    },
    infoItem: {},
    infoLabel: {
      fontSize: 12,
      color: isDark ? "#AAAAAA" : "#6b7280",
      marginBottom: 2,
    },
    infoValor: {
      fontSize: 15,
      fontWeight: "600",
      color: isDark ? "#E5E7EB" : "#374151",
    },
    infoValorDestaque: {
      fontSize: 18,
      fontWeight: "bold",
      color: isDark ? "#4ADE80" : "#059669",
    },
    itensResumo: {
      backgroundColor: isDark ? "#121212" : "#f9fafb",
      borderRadius: 8,
      padding: 10,
      marginBottom: 12,
      gap: 4,
    },
    itemLinha: { fontSize: 13, color: isDark ? "#D1D5DB" : "#4b5563" },
    acoes: { gap: 8 },
    botaoWhatsApp: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      backgroundColor: "#25D366",
      borderRadius: 8,
      paddingVertical: 12,
    },
    botaoWhatsAppTexto: { color: "#fff", fontWeight: "bold", fontSize: 15 },
    botaoPago: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      backgroundColor: isDark ? "#1A362C" : "#dcfce7",
      borderRadius: 8,
      paddingVertical: 12,
      borderWidth: 1,
      borderColor: isDark ? "#2B7A5E" : "#bbf7d0",
    },
    botaoPagoTexto: {
      color: isDark ? "#BBF7D0" : "#166534",
      fontWeight: "bold",
      fontSize: 15,
    },
    botaoExcluir: {
      backgroundColor: "#dc2626",
      width: 42,
      height: 42,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
    },
    botaoDesabilitado: { opacity: 0.6 },
    fab: {
      position: "absolute",
      right: 24,
      bottom: 24,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: "#2563eb",
      justifyContent: "center",
      alignItems: "center",
      elevation: 4,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
    },
  });
}
